import React, { useEffect, useState } from "react";
import { Button, Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import "../App.css";

function isUndefined([...array]): boolean {
  const filter = array.filter((value) => {
    return typeof value === "undefined";
  });
  if (filter.length === array.length) {
    return true;
  }
  return false;
}

function isNotUndefined([...array]): boolean {
  const filter = array.filter((value) => {
    return typeof value !== "undefined";
  });
  if (filter.length === array.length) {
    return true;
  }
  return false;
}

function isValidBoolen([...array]): boolean {
  const filter = array.filter((value) => {
    return value === true;
  });
  if (filter.length === array.length) {
    return true;
  }
  return false;
}

export interface IValidate {
  /**
   * 未入力の許可しない場合はtrue
   */
  required?: boolean;
  /**
   * 許可入力範囲
   */
  length?: { min?: number; max?: number };
  /**
   * 入力無効にする正規表現
   */
  invalid?: RegExp;
}

function isLengthSubmit(valueLength: number, validate: IValidate) {
  const { required, length } = validate;
  if (isUndefined([required, length])) {
    return undefined;
  }
  let isSubmit = false;
  let minLength = undefined;
  let maxLength = undefined;
  if (typeof required !== "undefined") {
    minLength = 1;
  }
  if (typeof length !== "undefined") {
    minLength = length.min;
    maxLength = length.max;
  }
  if (typeof minLength !== "undefined" && typeof maxLength !== "undefined") {
    if (minLength <= valueLength && valueLength <= maxLength) {
      isSubmit = true;
    }
  } else if (typeof minLength !== "undefined") {
    if (minLength <= valueLength) {
      isSubmit = true;
    }
  } else if (typeof maxLength !== "undefined") {
    if (valueLength <= maxLength) {
      isSubmit = true;
    }
  }
  return isSubmit;
}

function validateText(text: string, validate: IValidate) {
  const { invalid } = validate;
  const isLength = isLengthSubmit(text.length, validate);
  const isPattern = invalid ? !invalid.test(text) : undefined;
  if (isNotUndefined([isLength, isPattern])) {
    return isValidBoolen([isLength, isPattern]);
  } else if (isNotUndefined([isLength])) {
    return isValidBoolen([isLength]);
  } else if (isNotUndefined([isPattern])) {
    return isValidBoolen([isPattern]);
  }
  return false;
}

function getSubmitEnabled(textItems: InputTextItem[]) {
  const enableds = textItems.map((item) => {
    return item.enabled;
  });
  return (
    enableds.filter((enabled) => {
      return enabled === false;
    }).length === 0
  );
}

export interface InputItem {
  /**
   * 入力項目ID
   */
  id: string;
  /**
   * 入力項目タイプ
   */
  type?: string;
  /**
   * 入力項目ラベル
   */
  label: string;
  /**
   * 入力項目プレースホルダー
   */
  placeholder?: string;
  /**
   * 初期値
   */
  text?: string;
  /**
   * 最大入力長
   */
  maxLength?: number;
  /**
   * 入力判定条件
   */
  validate?: IValidate;
  /**
   * 入力値をカスタマイズする場合に使用します
   * @param {InputTextItem} item - 入力された項目
   * @param {string} value - 入力値
   */
  inputValue?: (item: InputTextItem, value: string) => void;
}

export interface InputTextItem {
  /**
   * 入力された項目ID
   */
  id: string;
  /**
   * 入力値
   */
  text: string;
  /**
   * 有効無効
   */
  enabled: boolean;
}

function getInputTextItem(
  item: InputItem,
  text: InputTextItem,
  value: string
): InputTextItem {
  if (item.inputValue) {
    item.inputValue(text, value);
  } else {
    text.text = value;
  }
  text.enabled = item.validate ? validateText(text.text, item.validate) : true;
  return text;
}

export interface PromptDialogParams {
  /**
   * タイトル
   */
  title: string;
  /**
   * 入力項目配列
   */
  items: InputItem[];
  /**
   * OKボタンを押した場合のCalllback
   */
  onOk?: (text: InputTextItem[]) => void;
  /**
   * Cancelボタンを押した場合のCalllback
   */
  onCancel?: () => void;
}

export interface PromptDialogProps extends PromptDialogParams {
  /**
   * ダイアログを表示する場合はtrue
   */
  show: boolean;
}

export default function PromptDialog(props: PromptDialogProps) {
  const { show, title, items, onOk, onCancel } = props;
  const { t } = useTranslation();
  const [texts, setTexts] = useState<InputTextItem[]>([]);
  const [submitEnabled, setSubmitEnabled] = useState<boolean>(false);

  useEffect(() => {
    const texts: InputTextItem[] = [];
    items.forEach((item) => {
      const text: InputTextItem = { id: item.id, text: "", enabled: false };
      const value = typeof item.text !== "undefined" ? item.text : "";
      texts.push(getInputTextItem(item, text, value));
    });
    setTexts(texts);
    setSubmitEnabled(getSubmitEnabled(texts));
  }, [items]);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (onOk) {
      onOk(texts);
    }
    setTexts([]);
  }

  function handleClose(): void {
    if (onCancel) {
      onCancel();
    }
    setTexts([]);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const { id, value } = e.target;
    const sliceTexts = texts.slice();
    const item = items.find((item) => {
      return item.id === id;
    });
    const text = sliceTexts.find((text) => {
      return text.id === id;
    });
    if (item && text) {
      const result = getInputTextItem(item, text, value);
      text.text = result.text;
      text.enabled = result.enabled;
      setTexts(sliceTexts);
      setSubmitEnabled(getSubmitEnabled(sliceTexts));
    }
  }

  const textboxs: JSX.Element[] = [];
  texts.forEach((text, i) => {
    const item = items.find((item) => {
      return item.id === text.id;
    });
    if (item) {
      const type =
        item.type && (item.type === "password" || item.type === "email")
          ? item.type
          : undefined;
      textboxs.push(
        <div key={i}>
          <Form.Group>
            <Form.Label>{item.label}</Form.Label>
            <Form.Control
              id={text.id}
              type={type}
              placeholder={item.placeholder}
              value={text.text}
              maxLength={item.maxLength}
              onChange={handleChange}
            />
          </Form.Group>
        </div>
      );
    }
  });
  const okBtn = submitEnabled ? (
    <Button className="dialog-button" variant="primary" type="submit">
      {t("prompt_ok")}
    </Button>
  ) : undefined;
  return (
    <Modal show={show} onHide={handleClose} animation={false}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <div>{textboxs}</div>
          <div className="dialog-buttons">
            {okBtn}
            <Button
              className="dialog-button"
              variant="secondary"
              onClick={handleClose}
            >
              {t("prompt_cancel")}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
